// V4.12 Phase F — Canvas price distribution (histogram).
// V4.23 — INTERACTIVE: every bar click reveals EXACTLY the listings of
// that price bucket (compact refs computed server-side from the same
// cascade result set — the frontend only renders).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

function fmtShort(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

function fmtEur(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}

function DistTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-950/95 px-2.5 py-1.5 text-xs">
      <div className="text-zinc-400">{fmtEur(p.min)} – {fmtEur(p.max)}</div>
      <div className="font-semibold text-amber-300">
        {t("widgets.listingCount", { count: p.count })}
      </div>
      {p.count > 0 && (
        <div className="pt-0.5 text-[10px] text-zinc-500">
          {t("widgets.bucketClickHint")}
        </div>
      )}
    </div>
  );
}

function sigColor(signal) {
  if (signal === "under_market" || signal === "strong_under_market")
    return "text-emerald-300";
  if (signal === "over_market" || signal === "strongly_over_market")
    return "text-rose-300";
  return "text-zinc-400";
}

function BucketItems({ bucket, onClose }) {
  const { t } = useTranslation("ai");
  const items = bucket.items || [];
  return (
    <div
      data-testid="ai-histogram-bucket-items"
      className="space-y-1 rounded-lg border border-amber-500/20 bg-zinc-950/60 p-2"
    >
      <div className="flex items-center justify-between text-[10px] text-zinc-500">
        <span>
          {fmtEur(bucket.min)} – {fmtEur(bucket.max)} ·{" "}
          {t("widgets.listingCount", { count: bucket.count })}
        </span>
        <button
          type="button"
          data-testid="ai-histogram-bucket-close"
          onClick={onClose}
          className="text-zinc-600 transition hover:text-zinc-300"
        >
          ✕
        </button>
      </div>
      {items.map((it) => (
        <Link
          key={it.id}
          to={`/listing/${it.id}`}
          data-testid={`ai-bucket-item-${it.id}`}
          className="flex items-center gap-2 rounded-md border border-zinc-800/70 bg-zinc-900/60 px-2 py-1.5 transition hover:border-amber-500/40"
        >
          {it.image ? (
            <img src={it.image} alt="" loading="lazy"
                 className="h-8 w-11 shrink-0 rounded object-cover" />
          ) : (
            <div className="h-8 w-11 shrink-0 rounded bg-zinc-800" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] text-zinc-200">{it.title}</div>
            <div className="truncate text-[10px] text-zinc-500">{it.location}</div>
          </div>
          <span className={`shrink-0 text-[11px] font-semibold ${sigColor(it.signal)}`}>
            {fmtEur(it.price)}
          </span>
        </Link>
      ))}
      {bucket.more > 0 && (
        <div className="px-1 text-[10px] text-zinc-600">
          {t("widgets.bucketMore", { count: bucket.more })}
        </div>
      )}
    </div>
  );
}

export function PriceDistributionWidget({ widget }) {
  const { t } = useTranslation("ai");
  const [selected, setSelected] = useState(null);
  const data = (widget.buckets || []).map((b, i) => ({
    ...b,
    idx: i,
    label: `${fmtShort(b.min)}–${fmtShort(b.max)}`,
  }));
  const st = widget.stats || {};
  if (!data.length) return null;
  const interactive = data.some((b) => (b.items || []).length > 0);
  const onBarClick = (entry) => {
    if (!interactive || !entry || !(entry.items || []).length) return;
    setSelected((cur) => (cur === entry.idx ? null : entry.idx));
  };
  return (
    <div
      data-testid="ai-widget-price-distribution"
      className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-2"
    >
      <div className="text-xs text-zinc-500 uppercase tracking-wide">
        {t("widgets.priceDistribution")}{widget.label ? ` · ${widget.label}` : ""}
      </div>
      <div className="h-44 sm:h-48">
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={160}>
          <BarChart
            data={data}
            margin={{ top: 4, right: 4, left: -22, bottom: 0 }}
            onClick={(state) => {
              const p = state?.activePayload?.[0]?.payload;
              if (p) onBarClick(p);
            }}
          >
            <XAxis
              dataKey="label"
              tick={{ fill: "#71717a", fontSize: 9 }}
              interval={1}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#71717a", fontSize: 10 }}
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<DistTooltip t={t} />}
              cursor={{ fill: "rgba(63,63,70,0.25)" }}
            />
            <Bar
              dataKey="count"
              radius={[4, 4, 0, 0]}
              onClick={onBarClick}
              className={interactive ? "cursor-pointer" : undefined}
            >
              {data.map((b) => (
                <Cell
                  key={b.idx}
                  fill="#f59e0b"
                  fillOpacity={selected == null ? 0.75 : (selected === b.idx ? 0.95 : 0.35)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {selected != null && data[selected] && (
        <BucketItems bucket={data[selected]} onClose={() => setSelected(null)} />
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-zinc-500">
        <span>{t("widgets.median")} <span className="font-semibold text-amber-400">{fmtEur(st.median)}</span></span>
        <span>{t("widgets.coreRange")} {fmtEur(st.p25)} – {fmtEur(st.p75)}</span>
        <span>{t("widgets.basisListings", { count: st.count })}</span>
      </div>
    </div>
  );
}
